import { LoyaltyTier, TIER_THRESHOLDS, nextTierFor } from '@/constants/loyalty'
import { wrapApiCall } from '@/lib/axios'
import { getCurrentBranchId } from '@/utils/helper/branch'
import type { AxiosInstance } from 'axios'
import {
  DashboardSummary,
  EarnPreview,
  EarningEvent,
  EarningEventCategory,
  MemberLoyalty,
  REWARDS_CATALOG,
  Redemption,
  RedemptionApprovalItem,
  RedemptionApprovalTab,
  RedemptionApprovalsResult,
  RedemptionHoldResult,
  RedemptionPreviewResult,
  RedemptionReceipt,
  RedemptionStatus,
  RedemptionStatusResult,
  Reward,
  RewardCategory,
  getMockDashboardSummary,
  getMockEarnPreview,
  getMockEarningEvents,
  getMockMemberLoyalty,
  getMockRedemptionApprovals,
  getMockRedemptionHold,
  getMockRedemptionPreview,
  getMockRedemptionReceipt,
  getMockRedemptions,
  getMockTiersForMembers,
} from './mock'

export type {
  DashboardEventBreakdown,
  DashboardRecentRedemption,
  DashboardSummary,
  EarnPreview,
  EarnPreviewRule,
  EarnPreviewTierUp,
  EarningEvent,
  EarningEventCategory,
  LoyaltyToastBlock,
  MemberLoyalty,
  Redemption,
  RedemptionApprovalItem,
  RedemptionApprovalTab,
  RedemptionApprovalsResult,
  RedemptionBlockReason,
  RedemptionHoldResult,
  RedemptionPreviewResult,
  RedemptionReceipt,
  RedemptionStatus,
  RedemptionStatusResult,
  Reward,
  RewardCategory,
} from './mock'

/**
 * Loyalty service. Every method serves the deterministic mock while
 * `VITE_LOYALTY_USE_MOCK` is unset/'true', and the real `/loyalty/*` BFF
 * (nova-ignite-backend feat/loyalty-1) when it is exactly 'false'. Adapters
 * below translate the BE wire entities into the FE shapes the components
 * already consume (see ./mock.ts for those shapes).
 *
 * The BE wants `branchId` as an explicit query/body param (the axios
 * `branch-id` HEADER is not read by the loyalty module) — resolved here via
 * `getCurrentBranchId()` so call sites stay clean.
 */
const USE_MOCK = import.meta.env.VITE_LOYALTY_USE_MOCK === 'true'

/**
 * Exposed for the rare surface that must know the data source (e.g. the
 * dashboard hides widgets that have no live endpoint yet). Components should
 * normally NOT branch on this — the service already falls back internally.
 */
export const LOYALTY_USE_MOCK = USE_MOCK

// ─── Tier / event-type mapping ────────────────────────────────────────────

/** Upstream Loyalty Platform tier name / opaque id → FitUP LoyaltyTier. */
const UPSTREAM_TIER_TO_FE: Record<string, LoyaltyTier> = {
  BRONZE: LoyaltyTier.BEGINNING,
  BEGINNING: LoyaltyTier.BEGINNING,
  SILVER: LoyaltyTier.SILVER,
  GOLD: LoyaltyTier.GOLD,
  PLATINUM: LoyaltyTier.PLATINUM,
}

/** Accepts FitUP codes, upstream names (Silver/Bronze), and opaque ids (`…-tier-silver`). */
export const mapUpstreamTier = (raw?: string | null): LoyaltyTier | undefined => {
  if (!raw) return undefined
  const seg = raw.includes('-tier-') ? (raw.split('-tier-').pop() ?? raw) : raw
  return UPSTREAM_TIER_TO_FE[seg.trim().toUpperCase()]
}

/**
 * Business event types the CRM knows by name, with the dictionary key that
 * labels them. Only types whose default English name would read badly need an
 * entry — everything else falls back to the caller's own wording.
 */
export const BUSINESS_EVENT_TYPE_LABEL_KEYS: Record<string, string> = {
  // Emitted when a PAID class booking is settled (cash/card/transfer/slip).
  'purchase.class_booking': 'loyalty-event-purchase-class-booking',
}

/** Dictionary key labelling a business event type, or `undefined` if unnamed. */
export const businessEventTypeLabelKey = (eventType: string): string | undefined =>
  BUSINESS_EVENT_TYPE_LABEL_KEYS[eventType]

/** BE business event type (e.g. 'class.attended') → FE earning category chip. */
export const eventTypeToCategory = (eventType: string): EarningEventCategory => {
  if (eventType.startsWith('checkin')) return 'CHECKIN'
  if (eventType.startsWith('class')) return 'CLASS'
  if (eventType.startsWith('pt')) return 'PT'
  if (eventType.startsWith('swimming')) return 'SWIMMING'
  if (eventType.startsWith('purchase')) return 'PURCHASE'
  return 'OTHER'
}

/** FE earning category chip → BE earning-history `category` filter param. */
const CATEGORY_TO_BE_FILTER: Partial<Record<EarningEventCategory, string>> = {
  CHECKIN: 'checkin',
  CLASS: 'class',
  PT: 'pt',
  SWIMMING: 'swimming',
  PURCHASE: 'purchase',
}

// ─── BE wire shapes (staff /loyalty/* contract, feat/loyalty-1) ───────────

interface BalanceWire {
  memberId: number
  tier: string
  tierName: string
  /** Spendable balance — net of active holds. */
  points: number
  /** Gross balance, ignoring holds — gates the redeem entry point. */
  availableBalance?: number
  nextTier?: string | null
  nextTierName?: string | null
  progress: {
    model: 'POINT' | 'TIME' | 'HYBRID'
    remaining: number
    unit: 'POINTS' | 'MONTHS'
    currentThreshold: number
    nextThreshold: number
  }
  hasAccount: boolean
  lastUpdated: string
  source: 'live' | 'stale' | 'unavailable'
}

interface EarningHistoryItemWire {
  businessEventId: string
  eventType: string
  title: string
  subtitle?: string
  label: { th: string; en: string }
  points: number
  runningBalance: number
  branchId?: number
  branchName?: string
  crossBranch: boolean
  timestamp: string
}

interface EarningHistoryWire {
  items: EarningHistoryItemWire[]
  total: number
  page: number
  pageSize: number
}

interface RedemptionHistoryItemWire {
  redemptionId: string
  rewardName: string
  rewardDescription?: string
  pointsUsed: number
  redemptionCode: string
  staffName?: string
  branchName: string
  crossBranch: boolean
  date: string
  status: RedemptionStatus
  pointsReturned: number
}

interface RedemptionHistoryWire {
  items: RedemptionHistoryItemWire[]
  total: number
  page: number
  pageSize: number
  nextCursor?: string
}

interface RewardItemWire {
  rewardId: string
  name: string
  description?: string
  type: 'SERVICE' | 'CLASS' | 'PRODUCT'
  pointCost: number
  inStock: boolean
  affordable: boolean
  quota?: number | null
  rewardType?: 'manual' | 'code' | 'perk'
  createdAt?: string
  redemptionCount?: number
}

interface RewardsWire {
  items: RewardItemWire[]
  availableCount: number
  total: number
}

/**
 * GET /loyalty/redemptions wire shape — mirrors the BE
 * `LoyaltyRedemptionApprovalsPage` / `LoyaltyRedemptionApprovalItem` entities
 * exactly (status is the underlying loyalty_redemption.status, not the tab).
 */
interface RedemptionApprovalItemWire {
  redemptionId: string
  code: string
  memberId: number
  memberName: string
  memberCode: string
  rewardId: string
  rewardName: string
  pointsUsed: number
  status: RedemptionStatus
  pointsReturned: number
  staffName?: string
  createdAt: string
  expiresAt?: string
}

interface RedemptionApprovalsWire {
  items: RedemptionApprovalItemWire[]
  total: number
  page: number
  pageSize: number
  counts: { pending: number; approved: number; rejected: number }
}

/** GET /loyalty/config wire shape (QTY-1717) — read-only since BE 15ae8150. */
export interface LoyaltyConfigWire {
  branchId: number
  tierModel: 'POINT' | 'TIME' | 'HYBRID'
  thresholds: Record<string, { points: number; months: number }>
  earningRules: Array<{ eventType: string; value: number; unit: string; enabled: boolean }>
  sync: { status?: string; syncedAt?: string; version: number }
}

// ─── Shared helpers ───────────────────────────────────────────────────────

/**
 * The BE caps pages at this size; one page covers the v1 history tabs, which
 * keep their existing client-side filter/paging UX (the BE `total` field is
 * page-scoped, so server-driven pagers can't show a true count yet anyway).
 */
const FULL_PAGE_SIZE = 100

const requireBranchId = (): number => {
  const branchId = getCurrentBranchId()
  if (branchId == null) throw new Error('No branch selected — loyalty call needs a branchId')
  return branchId
}

// ─── Live fetchers + BE→FE adapters ──────────────────────────────────────

/**
 * Batch tier lookup for the member list (QTY-1708). Members without a
 * resolvable tier are omitted, so the list shows "—".
 */
const fetchMemberTiers = async (
  client: AxiosInstance,
  memberIds: Array<string | number>
): Promise<Record<string, LoyaltyTier>> => {
  if (USE_MOCK) return getMockTiersForMembers(memberIds)
  if (memberIds.length === 0) return {}
  const { data } = await wrapApiCall(() =>
    client.post<{
      items: Array<{ memberId: number; tier: string; points: number }>
      unavailable: number[]
    }>('/loyalty/members/balance/batch', { memberIds: memberIds.map(Number) })
  )
  const result: Record<string, LoyaltyTier> = {}
  for (const item of data?.items ?? []) {
    const tier = mapUpstreamTier(item.tier)
    if (tier) result[String(item.memberId)] = tier
  }
  return result
}

/** GET /loyalty/members/:id/balance → loyalty band shape (QTY-1709). */
const fetchMemberLoyalty = async (
  client: AxiosInstance,
  memberId: string | number
): Promise<MemberLoyalty> => {
  if (USE_MOCK) return getMockMemberLoyalty(memberId)
  const { data } = await wrapApiCall(() =>
    client.get<BalanceWire>(`/loyalty/members/${Number(memberId)}/balance`, {
      // Required by the BE (raw Number() coercion server-side — a missing
      // param reaches Prisma as NaN and 500s), so fail fast like the siblings.
      params: { branchId: requireBranchId() },
    })
  )
  const tier = mapUpstreamTier(data.tier) ?? LoyaltyTier.BEGINNING
  const nextTier = mapUpstreamTier(data.nextTier) ?? nextTierFor(tier)
  const { nextThreshold, currentThreshold, remaining } = data.progress
  const span = nextThreshold - currentThreshold
  // Derive progress from the BE's model-aware `remaining` so the bar and the
  // "to next tier" figure agree.
  const progress = nextTier ? Math.min(1, Math.max(0, span > 0 ? (span - remaining) / span : 0)) : 1
  return {
    tier,
    balance: data.points,
    // Gross balance (ignores holds); falls back to spendable when the BE
    // doesn't send it. Used so a pending hold doesn't disable redeem.
    availableBalance: data.availableBalance ?? data.points,
    nextTier,
    toNextTier: nextTier ? Math.max(0, remaining) : 0,
    progress,
    nextTierThreshold: nextThreshold,
    hasAccount: data.hasAccount,
    source: data.source,
  }
}

export interface EarningHistoryParams {
  category?: EarningEventCategory
  /** ISO inclusive lower bound. */
  dateFrom?: string
  /** ISO exclusive upper bound. */
  dateTo?: string
  /** 1-based page (defaults to 1). Lets callers page the full history. */
  page?: number
  /** Rows per page (defaults to {@link FULL_PAGE_SIZE}). */
  pageSize?: number
}

/** GET /loyalty/members/:id/earning-history → earning tab rows (QTY-1710). */
const fetchMemberEarningEvents = async (
  client: AxiosInstance,
  memberId: string | number,
  params?: EarningHistoryParams
): Promise<EarningEvent[]> => {
  const page = params?.page ?? 1
  const pageSize = params?.pageSize ?? FULL_PAGE_SIZE
  if (USE_MOCK) {
    // Mirror the server-side filters so the tab's date chip behaves the same
    // in mock mode (category/search stay client-side in the tab itself).
    const dateFrom = params?.dateFrom
    const dateTo = params?.dateTo
    return getMockEarningEvents(memberId)
      .filter((e) => (!dateFrom || e.at >= dateFrom) && (!dateTo || e.at < dateTo))
      .slice((page - 1) * pageSize, page * pageSize)
  }
  const { data } = await wrapApiCall(() =>
    client.get<EarningHistoryWire>(`/loyalty/members/${Number(memberId)}/earning-history`, {
      params: {
        page,
        pageSize,
        category: params?.category ? CATEGORY_TO_BE_FILTER[params.category] : undefined,
        dateFrom: params?.dateFrom,
        dateTo: params?.dateTo,
      },
    })
  )
  return (data?.items ?? []).map((item) => ({
    id: item.businessEventId,
    category: eventTypeToCategory(item.eventType),
    title: item.title || item.label.en,
    subtitle: item.subtitle ?? '',
    at: item.timestamp,
    // branchName is a declared-but-unpopulated BE field today — components
    // must render an empty branch gracefully.
    branch: item.branchName ?? '',
    crossBranch: item.crossBranch,
    points: item.points,
    balanceAfter: item.runningBalance,
  }))
}

export interface RedemptionHistoryParams {
  status?: RedemptionStatus
  dateFrom?: string
  dateTo?: string
  /** 1-based page (defaults to 1). Lets callers page the full history. */
  page?: number
  /** Rows per page (defaults to {@link FULL_PAGE_SIZE}). */
  pageSize?: number
}

/** GET /loyalty/members/:id/redemption-history → redemption tab rows (QTY-1711). */
const fetchMemberRedemptions = async (
  client: AxiosInstance,
  memberId: string | number,
  params?: RedemptionHistoryParams
): Promise<Redemption[]> => {
  const page = params?.page ?? 1
  const pageSize = params?.pageSize ?? FULL_PAGE_SIZE
  if (USE_MOCK) {
    const dateFrom = params?.dateFrom
    const dateTo = params?.dateTo
    const status = params?.status
    return getMockRedemptions(memberId)
      .filter(
        (r) =>
          (!dateFrom || r.at >= dateFrom) &&
          (!dateTo || r.at < dateTo) &&
          (!status || r.status === status)
      )
      .slice((page - 1) * pageSize, page * pageSize)
  }
  const { data } = await wrapApiCall(() =>
    client.get<RedemptionHistoryWire>(`/loyalty/members/${Number(memberId)}/redemption-history`, {
      params: {
        page,
        pageSize,
        status: params?.status,
        dateFrom: params?.dateFrom,
        dateTo: params?.dateTo,
      },
    })
  )
  return (data?.items ?? []).map((item) => ({
    id: item.redemptionId,
    rewardName: item.rewardName,
    rewardSub: item.rewardDescription ?? '',
    cost: item.pointsUsed,
    status: item.status,
    at: item.date,
    branch: item.branchName ?? '',
    crossBranch: item.crossBranch,
    code: item.redemptionCode ?? '',
    staff: item.staffName ?? '',
    pointsReturned: item.pointsReturned,
  }))
}

const REWARD_TYPE_TO_CATEGORY: Record<RewardItemWire['type'], RewardCategory> = {
  SERVICE: 'SERVICE',
  CLASS: 'CLASS_TRAINING',
  PRODUCT: 'PRODUCT',
}

/** GET /loyalty/rewards → redemption modal catalogue (QTY-1716). */
const fetchRewardsCatalog = async (
  client: AxiosInstance,
  params?: { memberId?: string | number }
): Promise<Reward[]> => {
  if (USE_MOCK) return REWARDS_CATALOG
  const { data } = await wrapApiCall(() =>
    client.get<RewardsWire>('/loyalty/rewards', {
      params: {
        branchId: requireBranchId(),
        memberId: params?.memberId != null ? Number(params.memberId) : undefined,
      },
    })
  )
  return (data?.items ?? []).map((item) => ({
    id: item.rewardId,
    name: item.name,
    description: item.description ?? '',
    category: REWARD_TYPE_TO_CATEGORY[item.type] ?? 'SERVICE',
    cost: item.pointCost,
    stockLabel: !item.inStock
      ? 'Out of stock'
      : params?.memberId != null && !item.affordable
        ? 'Not enough points'
        : 'In stock',
    // The BE returns the FULL catalogue and marks each reward `affordable`
    // against the member's spendable balance — so unaffordable rewards are
    // SHOWN greyed-out rather than hidden. `available` drives the grey-out;
    // without a memberId (reward-management view) affordability is unknown,
    // so we don't gate on it.
    available: item.inStock && (params?.memberId == null || item.affordable),
    quota: item.quota,
    rewardType: item.rewardType,
    createdAt: item.createdAt,
    redemptionCount: item.redemptionCount,
  }))
}

/** POST /loyalty/redemptions/preview — read-only eligibility check. */
const previewRedemption = async (
  client: AxiosInstance,
  payload: { memberId: string | number; rewardId: string }
): Promise<RedemptionPreviewResult> => {
  if (USE_MOCK) return getMockRedemptionPreview(payload.memberId, payload.rewardId)
  const { data } = await wrapApiCall(() =>
    client.post<RedemptionPreviewResult>('/loyalty/redemptions/preview', {
      memberId: Number(payload.memberId),
      rewardId: payload.rewardId,
    })
  )
  return data
}

/** POST /loyalty/redemptions/hold — places the 15-min point hold. */
const holdRedemption = async (
  client: AxiosInstance,
  payload: { memberId: string | number; rewardId: string; rewardName?: string }
): Promise<RedemptionHoldResult> => {
  if (USE_MOCK) return getMockRedemptionHold(payload.memberId, payload.rewardId)
  const { data } = await wrapApiCall(() =>
    client.post<RedemptionHoldResult>('/loyalty/redemptions/hold', {
      memberId: Number(payload.memberId),
      rewardId: payload.rewardId,
      // Persisted on the BE local row so redemption history shows the name
      // without a catalogue lookup (and survives the reward being archived).
      rewardName: payload.rewardName,
      branchId: requireBranchId(),
    })
  )
  return data
}

/** POST /loyalty/redemptions/:id/confirm — debits the held points. */
const confirmRedemption = async (
  client: AxiosInstance,
  redemptionId: string,
  mockContext?: { pointsUsed?: number; rewardName?: string; code?: string }
): Promise<RedemptionReceipt> => {
  if (USE_MOCK) return getMockRedemptionReceipt(redemptionId, 'CONFIRMED', mockContext)
  const { data } = await wrapApiCall(() =>
    client.post<RedemptionReceipt>(`/loyalty/redemptions/${redemptionId}/confirm`)
  )
  return data
}

/**
 * POST /loyalty/redemptions/:id/cancel. `reason: 'FAILED'` marks a fulfilment
 * failure (points return either way; only the local terminal status differs).
 */
const cancelRedemption = async (
  client: AxiosInstance,
  redemptionId: string,
  reason?: 'CANCELLED' | 'FAILED',
  mockContext?: { pointsUsed?: number; rewardName?: string; code?: string }
): Promise<RedemptionReceipt> => {
  if (USE_MOCK) return getMockRedemptionReceipt(redemptionId, reason ?? 'CANCELLED', mockContext)
  const { data } = await wrapApiCall(() =>
    client.post<RedemptionReceipt>(`/loyalty/redemptions/${redemptionId}/cancel`, undefined, {
      params: reason ? { reason } : {},
    })
  )
  return data
}

/** POST /loyalty/redemptions/confirm-by-code — confirm from a member's LINE QR code. */
const confirmRedemptionByCode = async (
  client: AxiosInstance,
  code: string
): Promise<RedemptionReceipt> => {
  if (USE_MOCK) return getMockRedemptionReceipt(`red_mock_${code}`, 'CONFIRMED', { code })
  const { data } = await wrapApiCall(() =>
    client.post<RedemptionReceipt>('/loyalty/redemptions/confirm-by-code', {
      code,
      branchId: requireBranchId(),
    })
  )
  return data
}

/** GET /loyalty/redemptions/:id — poll a hold's state / countdown. */
const getRedemptionStatus = async (
  client: AxiosInstance,
  redemptionId: string
): Promise<RedemptionStatusResult> => {
  if (USE_MOCK) return { status: 'HELD', remainingSeconds: 900 }
  const { data } = await wrapApiCall(() =>
    client.get<RedemptionStatusResult>(`/loyalty/redemptions/${redemptionId}`)
  )
  return data
}

/**
 * GET /loyalty/redemptions — staff approvals queue (branch-scoped, served from
 * the LOCAL loyalty_redemption shadow rows; no upstream call). The wire shape
 * already matches the FE row shape 1:1, so the adapter is a pass-through plus
 * `branchId` injection. The BE doesn't send a member tier yet — left undefined.
 */
const fetchRedemptionApprovals = async (
  client: AxiosInstance,
  params: {
    status: RedemptionApprovalTab
    search?: string
    page?: number
    pageSize?: number
  }
): Promise<RedemptionApprovalsResult> => {
  if (USE_MOCK) return getMockRedemptionApprovals(params)
  const { data } = await wrapApiCall(() =>
    client.get<RedemptionApprovalsWire>('/loyalty/redemptions', {
      params: {
        branchId: requireBranchId(),
        status: params.status,
        search: params.search?.trim() ? params.search.trim() : undefined,
        page: params.page,
        pageSize: params.pageSize,
      },
    })
  )
  const items: RedemptionApprovalItem[] = (data?.items ?? []).map((item) => ({
    redemptionId: item.redemptionId,
    code: item.code,
    memberId: item.memberId,
    memberName: item.memberName,
    memberCode: item.memberCode,
    rewardId: item.rewardId,
    rewardName: item.rewardName,
    pointsUsed: item.pointsUsed,
    status: item.status,
    pointsReturned: item.pointsReturned,
    staffName: item.staffName,
    createdAt: item.createdAt,
    expiresAt: item.expiresAt,
  }))
  return {
    items,
    total: data?.total ?? items.length,
    page: data?.page ?? params.page ?? 1,
    pageSize: data?.pageSize ?? params.pageSize ?? 20,
    counts: data?.counts ?? { pending: 0, approved: 0, rejected: 0 },
  }
}

/**
 * GET /loyalty/earn/preview (QTY-1713/1714/1715). With `memberId` the BE
 * quotes upstream (per-tier multiplier-aware) and predicts tier-up; without
 * it the local base rule applies. `spendAmount` is required for
 * `purchase.package` (points = spend ÷ 100 × rate).
 */
const fetchEarnPreview = async (
  client: AxiosInstance,
  params: { eventType: string; spendAmount?: number; memberId?: string | number }
): Promise<EarnPreview> => {
  if (USE_MOCK) return getMockEarnPreview(params)
  const { data } = await wrapApiCall(() =>
    client.get<EarnPreview>('/loyalty/earn/preview', {
      params: {
        eventType: params.eventType,
        branchId: requireBranchId(),
        spendAmount: params.spendAmount != null ? Math.floor(params.spendAmount) : undefined,
        memberId: params.memberId != null ? Number(params.memberId) : undefined,
      },
    })
  )
  return data
}

/** GET /loyalty/dashboard/summary (QTY-1718). `date` defaults to today on the BE. */
const fetchDashboardSummary = async (
  client: AxiosInstance,
  params?: { date?: string }
): Promise<DashboardSummary> => {
  if (USE_MOCK) return getMockDashboardSummary()
  const { data } = await wrapApiCall(() =>
    client.get<DashboardSummary>('/loyalty/dashboard/summary', {
      params: { branchId: requireBranchId(), date: params?.date },
    })
  )
  return data
}

/**
 * GET /loyalty/config (QTY-1717) — READ ONLY.
 *
 * `PUT /loyalty/config` was removed from the BE on 2026-06-10 (commit
 * 15ae8150): earning-rule / tier config is owned by the Loyalty Platform team
 * via their admin console, not synced from FitUP. Until the BE repoints this
 * read at upstream `rules:read`, live responses are the repository defaults.
 */
const fetchLoyaltyConfig = async (client: AxiosInstance): Promise<LoyaltyConfigWire> => {
  if (USE_MOCK) {
    return {
      branchId: getCurrentBranchId() ?? 1,
      tierModel: 'POINT',
      thresholds: {
        BEGINNING: { points: 0, months: 0 },
        SILVER: { points: TIER_THRESHOLDS[LoyaltyTier.SILVER], months: 6 },
        GOLD: { points: TIER_THRESHOLDS[LoyaltyTier.GOLD], months: 18 },
        PLATINUM: { points: TIER_THRESHOLDS[LoyaltyTier.PLATINUM], months: 36 },
      },
      earningRules: [
        { eventType: 'checkin.club', value: 5, unit: 'per_event', enabled: true },
        { eventType: 'class.attended', value: 25, unit: 'per_event', enabled: true },
        { eventType: 'pt.attended', value: 50, unit: 'per_session', enabled: true },
        { eventType: 'swimming.attended', value: 15, unit: 'per_session', enabled: false },
        { eventType: 'purchase.package', value: 1, unit: 'per_100_baht', enabled: true },
        { eventType: 'purchase.class_booking', value: 1, unit: 'per_100_baht', enabled: true },
        { eventType: 'feedback.submitted', value: 10, unit: 'per_event', enabled: true },
        { eventType: 'referral.signed_up', value: 200, unit: 'per_event', enabled: false },
        { eventType: 'bonus.birthday', value: 100, unit: 'one_time_year', enabled: true },
      ],
      sync: { status: 'SYNCED', version: 1 },
    }
  }
  const { data } = await wrapApiCall(() =>
    client.get<LoyaltyConfigWire>('/loyalty/config', { params: { branchId: requireBranchId() } })
  )
  return data
}

/** GET/PUT /loyalty/credentials status — the raw key is never returned, only a
 *  masked form (e.g. `lpsk_test_••••XXXX`) and whether one is configured. */
export interface LoyaltyCredentialStatus {
  isConfigured: boolean
  maskedApiKey: string | null
}

/** GET /loyalty/credentials → masked status (no branch param; tenant-scoped). */
const fetchLoyaltyCredentialStatus = async (
  client: AxiosInstance
): Promise<LoyaltyCredentialStatus> => {
  if (USE_MOCK) return { isConfigured: true, maskedApiKey: 'lpsk_test_••••••CATM' }
  const { data } = await wrapApiCall(() =>
    client.get<LoyaltyCredentialStatus>('/loyalty/credentials')
  )
  return data
}

/** PUT /loyalty/credentials — write a new loyaltyApiKey (set up or rotate). */
const setLoyaltyCredential = async (
  client: AxiosInstance,
  apiKey: string
): Promise<LoyaltyCredentialStatus> => {
  if (USE_MOCK)
    return { isConfigured: true, maskedApiKey: `${apiKey.slice(0, 10)}••••${apiKey.slice(-4)}` }
  const { data } = await wrapApiCall(() =>
    client.put<LoyaltyCredentialStatus>('/loyalty/credentials', { apiKey })
  )
  return data
}

export const LoyaltyService = (client: AxiosInstance) => ({
  getMemberTiers: (memberIds: Array<string | number>) => fetchMemberTiers(client, memberIds),
  getCredentialStatus: () => fetchLoyaltyCredentialStatus(client),
  setCredential: (apiKey: string) => setLoyaltyCredential(client, apiKey),
  getMemberLoyalty: (memberId: string | number) => fetchMemberLoyalty(client, memberId),
  getMemberEarningEvents: (memberId: string | number, params?: EarningHistoryParams) =>
    fetchMemberEarningEvents(client, memberId, params),
  getMemberRedemptions: (memberId: string | number, params?: RedemptionHistoryParams) =>
    fetchMemberRedemptions(client, memberId, params),
  getRewardsCatalog: (params?: { memberId?: string | number }) =>
    fetchRewardsCatalog(client, params),
  previewRedemption: (payload: { memberId: string | number; rewardId: string }) =>
    previewRedemption(client, payload),
  holdRedemption: (payload: { memberId: string | number; rewardId: string; rewardName?: string }) =>
    holdRedemption(client, payload),
  confirmRedemption: (
    redemptionId: string,
    mockContext?: { pointsUsed?: number; rewardName?: string; code?: string }
  ) => confirmRedemption(client, redemptionId, mockContext),
  cancelRedemption: (
    redemptionId: string,
    reason?: 'CANCELLED' | 'FAILED',
    mockContext?: { pointsUsed?: number; rewardName?: string; code?: string }
  ) => cancelRedemption(client, redemptionId, reason, mockContext),
  confirmRedemptionByCode: (code: string) => confirmRedemptionByCode(client, code),
  getRedemptionStatus: (redemptionId: string) => getRedemptionStatus(client, redemptionId),
  getRedemptionApprovals: (params: {
    status: RedemptionApprovalTab
    search?: string
    page?: number
    pageSize?: number
  }) => fetchRedemptionApprovals(client, params),
  getEarnPreview: (params: {
    eventType: string
    spendAmount?: number
    memberId?: string | number
  }) => fetchEarnPreview(client, params),
  getDashboardSummary: (params?: { date?: string }) => fetchDashboardSummary(client, params),
  getLoyaltyConfig: () => fetchLoyaltyConfig(client),
})
